import React, { ChangeEvent } from "react";
import clsx from "clsx";
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";

import Autocomplete from "../Autocomplete";
import ChevronBigDown from "@tiny/icons/generated/ChevronBigDown";
import { AutocompleteFieldProps } from "./AutocompleteField.types";
import { extractAutcompleteProps } from "./Autocomplete.helpers";
import { useChipStyles, useStyles } from "./AutocompleteField.styles";
import useRandom from "../hooks/useRandom";
import TextFieldBase from "../TextFieldBase";
import { InfoTooltip } from "..";
import { Chip } from "@mui/material";

function AutocompleteField(props: AutocompleteFieldProps): JSX.Element {
  const {
    onChangeValue,
    inputAutoComplete,
    inputAriaLabel,
    disableInputChange,
    disabled,
    size,
    info,
    infoIcon,
    toolTipModifiers,
    getOptionLabel,
    multiple,
    ...other
  } = props;

  const classes = useStyles();

  const chipClasses = useChipStyles();

  const autoCompleteRandom = useRandom();

  const [{ onChange, ...autocompleteProps }, textFieldProps] =
    extractAutcompleteProps(other);

  const handleChange = (
    event: React.SyntheticEvent,
    value: any,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any> | undefined
  ) => {
    if (onChange) {
      onChange(event, value, reason, details);
    }
    if (onChangeValue) {
      onChangeValue(value);
    }
  };

  const renderInput = (params: AutocompleteRenderInputParams) => {
    const {
      InputProps: { ref, className, endAdornment, ...InputPropsOther },
      ...otherInputProps
    } = params;

    const inputProps = {
      ...otherInputProps.inputProps,
      autoComplete: inputAutoComplete || autoCompleteRandom,
      ["aria-label"]: inputAriaLabel,
      readOnly: disableInputChange,
    };

    const infoTooltip = info ? (
      <InfoTooltip title={info} icon={infoIcon} modifiers={toolTipModifiers} />
    ) : null;

    const textFieldClassName = clsx(
      classes.textfield,
      {
        [classes.textfieldDisabled]: disabled,
        [classes.textfieldSmall]: size === "small",
        [classes.textfieldInfo]: info,
        [classes.textfieldMultiple]: multiple,
      },
      props.className
    );

    return (
      <TextFieldBase
        {...textFieldProps}
        {...otherInputProps}
        className={textFieldClassName}
        InputProps={{
          ...InputPropsOther,
          endAdornment: (
            <>
              {endAdornment}
              {infoTooltip}
            </>
          ),
          ref,
        }}
        inputProps={inputProps}
        size={size}
      />
    );
  };

  const iconClassName = clsx(classes.arrow, {
    [classes.arrowSmall]: size === "small",
    [classes.arrowDisabled]: disabled,
  });

  return (
    <Autocomplete
      {...autocompleteProps}
      disabled={disabled}
      onChange={handleChange}
      renderInput={renderInput}
      size={size}
      multiple={multiple}
      getOptionLabel={getOptionLabel}
      popupIcon={<ChevronBigDown className={iconClassName} />}
      renderTags={(value: any[], getTagProps) =>
        value.map((option: any, index: number) => (
          <Chip
            {...getTagProps({ index })}
            key={index}
            size={size}
            label={getOptionLabel?.(option)}
            classes={chipClasses}
          />
        ))
      }
    />
  );
}

export default AutocompleteField;
